import React, { lazy, Component, Suspense } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  Redirect,
  Route,
  Switch
} from "react-router-dom";
import classNames from "classnames";

import Spinner from "@ui/spinner";

import "./discovery.less";

import NotificationHandler from "./notification_handler";

// Root components
import TopBar from "./top_bar";
import { Navigation } from "./navigation";

// Modals
const ReleaseBannerModal = lazy(() =>
  import(/* webpackChunkName: "release_banner_modal" */ "./modals/releaseBanner"));

// Asynchronously loaded routes
const AdminPanel = lazy(() =>
  import(/* webpackChunkName: "admin_panel" */ "./admin/admin"));
const BatchStepper = lazy(() =>
  import(/* webpackChunkName: "batch_wizard" */ "./batch_wizard/batch_stepper"));
const BatchSummaryReportWrapper = lazy(() =>
  import(/* webpackChunkName: "batch_summary" */ "./batch_summary_report_wrapper"));
const BatchWorkspaces = lazy(() =>
  import(/* webpackChunkName: "batch_workspaces" */ "./batch_workspaces"));
const CustomCategories = lazy(() =>
  import(/* webpackChunkName: "custom_categories" */ "./custom_categories/custom_categories"));
const DataExchange = lazy(() =>
  import(/* webpackChunkName: "data_exchange" */ "./data_exchange"));
const FileTransfers = lazy(() =>
  import(/* webpackChunkName: "file_transfers" */ "./file_transfers/file_transfers"));
const Gateway = lazy(() =>
  import(/* webpackChunkName: "gateway" */ "../overview/overview"));
const OfflineBatches = lazy(() =>
  import(/* webpackChunkName: "offline_batches" */ "./offline_batches/offline_batches"));
const Pricing = lazy(() =>
  import(/* webpackChunkName: "pricing" */ "../pricing/pricing"));
const Reporting = lazy(() =>
  import(/* webpackChunkName: "reporting" */ "../reporting/report"));
const ReviewAndConfirmStandAlone = lazy(() =>
  import(/* webpackChunkName: "batch_wizard" */ "./batch_wizard/review_and_confirm_stand_alone"));
const Releases = lazy(() =>
  import(/* webpackChunkName: "releases" */ "./platform_feature_releases"));
const Workspace = lazy(() =>
  import(/* webpackChunkName: "workspace" */ "./batch_workspaces/workspace"));
const Settings = lazy(() =>
  import(/* webpackChunkName: "settings" */ "./settings"));
const StylesPreview = lazy(() =>
  import(/* webpackChunkName: "styles_preview" */ "../styles_preview/styles_preview"));

class Discovery extends Component {
  static propTypes = {
    currentUser: PropTypes.shape({
      id: PropTypes.number.isRequired,
      permissions: PropTypes.shape({
        can_manage_offline_batches: PropTypes.bool.isRequired,
        can_show_admin: PropTypes.bool.isRequired,
        can_show_gateway: PropTypes.bool.isRequired,
        can_show_pricing: PropTypes.bool.isRequired,
        can_show_reporting: PropTypes.bool.isRequired,
        can_view_categories: PropTypes.bool.isRequired
      }),
      pre_workspace_launch_user: PropTypes.bool.isRequired
    })
  };

  state = {
    narrow: false,
    isModalOpen: null
  };

  componentDidMount() {
    const { currentUser } = this.props;
    const { currentOrganization } = this.props;
    const { pre_workspace_launch_user } = currentUser;

    this.updateOnResize();
    window.addEventListener("resize", this.updateOnResize);

    if (pre_workspace_launch_user) {
      this.openReleaseBannerModal();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateOnResize);
  }

  updateOnResize = () => {
    this.setState((prevState) => {
      const narrow = window.innerWidth <= 1100;

      if (narrow === prevState.narrow) {
        return null;
      }

      return {
        narrow
      };
    });
  }

  openReleaseBannerModal = () => this.setState({ isModalOpen: true });

  closeReleaseBannerModal = () => this.setState({ isModalOpen: false });

  toggleSidebar = () => {
    this.setState(prevState => ({ narrow: !prevState.narrow }));
  };

  render() {
    const { currentUser } = this.props;
    const { currentOrganization } = this.props;
    const { isModalOpen, narrow } = this.state;

    if (!currentUser) {
      return null;
    }

    const {
      can_manage_offline_batches,
      can_show_admin,
      can_show_file_transfers,
      can_show_gateway,
      can_show_reporting,
      can_view_categories,
      can_show_pricing
    } = currentUser.permissions;


    // **** NOTE: If component is extended with `with_back_button`, then it will automatically have the closest history.
    // No need to send it in through the render function for the given route below
    return (
      <div
        className={classNames("Discovery", {
          "Discovery--navigation-expanded": !narrow
        })}
      >
        <NotificationHandler />
        <Navigation narrow={narrow} toggleSidebar={this.toggleSidebar} />
        <div className="Discovery__mainWrapper">
          <TopBar />
          <main>
            <Suspense fallback={<span />}>
              {isModalOpen !== null ? (
                <ReleaseBannerModal
                  isOpen={isModalOpen}
                  closeModal={this.closeReleaseBannerModal}
                />
              ) : null}
            </Suspense>
            <Suspense
              fallback={
                <div className="adriatic-spinner">
                  <Spinner big multicolor />
                </div>
              }
            >
              <Switch>
                <Redirect exact from="/discovery" to="/discovery/workspaces" />
                <Route path="/discovery/catalog" component={DataExchange} />
                <Route
                  path="/discovery/workspaces/:id/new_batch"
                  component={BatchStepper}
                />
                <Route
                  path="/discovery/workspaces/:id/edit_batch/:batch_id"
                  component={BatchStepper}
                />
                <Route
                  path="/discovery/workspaces/:id/review_and_confirm/:batch_id"
                  component={ReviewAndConfirmStandAlone}
                />
                <Route path="/discovery/workspaces/:id" component={Workspace} />
                <Route
                  path="/discovery/workspaces"
                  component={BatchWorkspaces}
                />
                <Route
                  path="/discovery/batch_summary/:batch_id"
                  component={BatchSummaryReportWrapper}
                />
                {can_show_admin && (
                  <Route path="/discovery/admin" component={AdminPanel} />
                )}
                {can_show_admin && (
                  <Route path="/discovery/styles" component={StylesPreview} />
                )}
                {can_show_file_transfers && (
                  <Route
                    path="/discovery/file_transfers"
                    component={FileTransfers}
                  />
                )}
                {can_show_gateway && (
                  <Route path="/discovery/gateway" component={Gateway} />
                )}
                {can_show_pricing && (
                  <Route path="/discovery/pricing" component={Pricing} />
                )}
                {can_show_reporting && (
                  <Route path="/discovery/reporting" component={Reporting} />
                )}
                {can_view_categories && (
                  <Route
                    path="/discovery/custom_categories"
                    component={CustomCategories}
                  />
                )}
                {can_manage_offline_batches && (
                  <Route
                    path="/discovery/offline_batches/:id"
                    component={OfflineBatches}
                  />
                )}
                {can_manage_offline_batches && (
                  <Route
                    path="/discovery/offline_batches"
                    component={OfflineBatches}
                  />
                )}
                <Route path="/discovery/releases" component={Releases} />
                <Route path="/discovery/settings" component={Settings} />
                {/* when none of the above match, <NoMatch> will be rendered */}
                <Route
                  render={props => (
                    <div>
                      We cannot find the page in question. The path '
                      {props.location.pathname}' does not exist.
                    </div>
                  )}
                />
              </Switch>
            </Suspense>
          </main>
        </div>
      </div>
    );
  }
}

export default connect(
  store => ({
    currentUser: store.session.data && store.session.data.user,
    currentOrganization: store.session.data && store.session.data.organization
  })
)(Discovery);
